// mds-dojo — FAITHFUL port of dojo/_base/declare (the real C3 multiple-inheritance
// runtime), adapted for ESM strict mode.
//
// WHY this exists (vs the compose()/mdsWidget() system in ./declare.ts):
//   The app's codemod emits `class X extends compose(A, ...factories)` — a single
//   spine + mixin FACTORIES. Real dojo/dijit source instead does
//   `declare("dijit.Foo", [A, B, C], {props})` where A,B,C are all plain classes
//   and dojo computes their C3 linearization + a `this.inherited(arguments)` that
//   walks that MRO. To port dijit/dojox source FAITHFULLY (not freestyle) we need
//   that exact algorithm. This module IS that algorithm, ported line-for-line from
//   references/dojo/_base/declare.js.
//
// The lifecycle falls out for free: dojo's constructor machinery calls
// `this.postscript(args)` after the ctor chain, and dijit `_WidgetBase.postscript`
// = `create`. So a widget built on this runtime runs postMixInProperties →
// buildRendering → postCreate → startup natively, no construct-trap Proxy.
//
// STRICT-MODE ADAPTATION (the only deviations from dojo source):
//   1. `arguments.callee` is illegal in ESM strict. dojo's inherited() used it to
//      identify the calling method. We instead maintain `this.__declareCaller`,
//      set by a thin wrapper around every declared method (safeMixin), and read it
//      where dojo read `args.callee`. Behaviour is identical for the universal
//      pattern `this.inherited(arguments)` invoked from a declared method.
//      Consequence (matches dojo's OWN documented strict-mode rule): calling
//      `this.inherited('name', arguments)` from a RAW (undeclared / directly
//      prototype-assigned) function can't self-identify — dojo requires the caller
//      be passed in strict mode (`this.inherited(fn, arguments)`). We support that
//      explicit form.
//   2. `this instanceof arguments.callee` (the "called without new" guard) → a
//      captured self-reference of the generated constructor.
//
// Everything else — c3mro, chained/single/simple constructors, `-chains-`
// before/after chaining, forceNew/applyNew, safeMixin, extend, createSubclass,
// isInstanceOf, `_meta` — is faithful to dojo 1.x.

import lang from './lang';
// Type-only (erased at runtime → no import cycle with declare.ts, which value-imports
// this module). Reuses compose()'s proven base-list→instance mapping for declare()'s
// typed return.
import type { BaseInstance, ComposedInstance } from './declare';

export type Ctor = new (...args: any[]) => any;

const mix = lang.mixin;
const op: any = Object.prototype;
const opts = op.toString;
let counter = 0;
const cname = 'constructor';

// forceNew's do-nothing constructor. dojo uses `new Function` when CSP allows (no
// closure); a plain empty function is behaviourally identical and CSP-safe.
const xtor: any = function () { /* no-op */ };

function err(msg: string, cls?: string): never {
  throw new Error('declare' + (cls ? ' ' + cls : '') + ': ' + msg);
}

// ---- C3 Method Resolution Order --------------------------------------------
// (http://www.python.org/download/releases/2.3/mro/)
function c3mro(bases: any[], className: string): any[] {
  const result: any[] = [];
  const roots: any[] = [{ cls: 0, refs: [] }];
  const nameMap: any = {};
  let clsCount = 1;
  let l = bases.length;
  let i = 0;
  let j: number; let lin: any; let base: any; let top: any; let proto: any; let rec: any; let name: string; let refs: any;

  // build a list of bases naming them if needed
  for (; i < l; ++i) {
    base = bases[i];
    if (!base) {
      err('mixin #' + i + ' is unknown. Did you use dojo.require to pull it in?', className);
    } else if (opts.call(base) != '[object Function]') {
      err('mixin #' + i + ' is not a callable constructor.', className);
    }
    lin = base._meta ? base._meta.bases : [base];
    top = 0;
    // add bases to the name map
    for (j = lin.length - 1; j >= 0; --j) {
      proto = lin[j].prototype;
      if (!proto.hasOwnProperty('declaredClass')) {
        proto.declaredClass = 'uniqName_' + (counter++);
      }
      name = proto.declaredClass;
      if (!nameMap.hasOwnProperty(name)) {
        nameMap[name] = { count: 0, refs: [], cls: lin[j] };
        ++clsCount;
      }
      rec = nameMap[name];
      if (top && top !== rec) {
        rec.refs.push(top);
        ++top.count;
      }
      top = rec;
    }
    ++top.count;
    roots[0].refs.push(top);
  }

  // remove classes without external references recursively
  while (roots.length) {
    top = roots.pop();
    result.push(top.cls);
    --clsCount;
    // optimization: follow a single-linked chain
    while (refs = top.refs, refs.length == 1) {
      top = refs[0];
      if (!top || --top.count) {
        // branch or end of chain => do not end to roots
        top = 0;
        break;
      }
      result.push(top.cls);
      --clsCount;
    }
    if (top) {
      // branch
      for (i = 0, l = refs.length; i < l; ++i) {
        top = refs[i];
        if (!--top.count) {
          roots.push(top);
        }
      }
    }
  }
  if (clsCount) {
    err("can't build consistent linearization", className);
  }

  // calculate the superclass offset
  base = bases[0];
  result[0] = base
    ? base._meta && base === result[result.length - base._meta.bases.length]
      ? base._meta.bases.length : 1 : 0;

  return result;
}

// ---- inherited() -----------------------------------------------------------
// Faithful to dojo, except `caller` comes from `this.__declareCaller` (set by the
// method wrapper) rather than `arguments.callee`. Explicit-name and explicit-
// caller forms are supported for strict-mode raw callers.
function inherited(this: any, argsOrCaller: any, a?: any, f?: any, g?: any): any {
  let name: any; let chains: any; let bases: any; let caller: any; let meta: any; let base: any; let proto: any; let opf: any; let pos: any;
  const cache = this._inherited = this._inherited || {};

  let args: any = argsOrCaller;

  // crack arguments — dojo shapes:
  //   inherited(arguments)
  //   inherited(name, arguments[, newArgs])
  //   inherited(callerFn, arguments[, newArgs])   (strict-mode explicit caller)
  //   inherited(name, callerFn, arguments[, newArgs])
  if (typeof args === 'string') {
    name = args;
    args = a; a = f; f = g;
  }

  if (typeof args === 'function') {
    // explicit caller (strict mode)
    caller = args;
    args = a; a = f;
  } else {
    // strict-mode-safe substitute for `args.callee`
    caller = this.__declareCaller;
  }

  name = name || (caller && caller.nom);
  if (!name) {
    err("can't deduce a name to call inherited()", this.declaredClass);
  }
  f = g = 0;

  meta = this.constructor._meta;
  if (!meta) {
    // The instance's constructor is NOT a declare class — e.g. a native
    // `class X extends compose(declareBase)` leaf (the app's codemod-emitted
    // widgets use native `super`, not this.inherited). A declare-authored method
    // reached via native super still calls this.inherited(); resolve it against
    // the nearest declare ancestor's MRO by walking the prototype chain.
    let p: any = Object.getPrototypeOf(this);
    while (p && !(p.constructor && p.constructor._meta)) { p = Object.getPrototypeOf(p); }
    meta = p && p.constructor && p.constructor._meta;
    if (!meta) {
      err("can't deduce a class to call inherited() from", this.declaredClass);
    }
  }
  bases = meta.bases;

  pos = cache.p;
  if (name != cname) {
    // method
    if (cache.c !== caller) {
      // cache bust
      pos = 0;
      base = bases[0];
      meta = base._meta;
      if (meta.hidden[name] !== caller) {
        // error detection
        chains = meta.chains;
        if (chains && typeof chains[name] == 'string') {
          err('calling chained method with inherited: ' + name, this.declaredClass);
        }
        // find caller
        do {
          meta = base._meta;
          proto = base.prototype;
          if (meta && (proto[name] === caller && proto.hasOwnProperty(name) || meta.hidden[name] === caller)) {
            break;
          }
        } while (base = bases[++pos]); // intentional assignment
        pos = base ? pos : -1;
      }
    }
    // find next
    base = bases[++pos];
    if (base) {
      proto = base.prototype;
      if (base._meta && proto.hasOwnProperty(name)) {
        f = proto[name];
      } else {
        opf = op[name];
        do {
          proto = base.prototype;
          f = proto[name];
          if (f && (base._meta ? proto.hasOwnProperty(name) : f !== opf)) {
            break;
          }
        } while (base = bases[++pos]); // intentional assignment
      }
    }
    f = base && f || op[name];
  } else {
    // constructor
    if (cache.c !== caller) {
      // cache bust
      pos = 0;
      meta = bases[0]._meta;
      if (meta && meta.ctor !== caller) {
        // error detection
        chains = meta.chains;
        if (!chains || chains.constructor !== 'manual') {
          err('calling chained constructor with inherited', this.declaredClass);
        }
        // find caller
        while (base = bases[++pos]) { // intentional assignment
          meta = base._meta;
          if (meta && meta.ctor === caller) {
            break;
          }
        }
        pos = base ? pos : -1;
      }
    }
    // find next
    while (base = bases[++pos]) { // intentional assignment
      meta = base._meta;
      f = meta ? meta.ctor : base;
      if (f) {
        break;
      }
    }
    f = base && f;
  }

  // cache the found super method
  cache.c = f;
  cache.p = pos;

  // now we have the result
  if (f) {
    return a === true ? f : f.apply(this, a || args);
  }
  // intentionally no return if a super method was not found
}

function getInherited(this: any, name: any, args: any, a?: any): any {
  if (typeof name === 'string') {
    if (typeof args === 'function') {
      return this.__inherited(name, args, a, true);
    }
    return this.__inherited(name, args, true);
  } else if (typeof name === 'function') {
    return this.__inherited(name, args, true);
  }
  return this.__inherited(name, true);
}

// emulation of "instanceof"
function isInstanceOf(this: any, cls: any): boolean {
  const bases = this.constructor._meta.bases;
  for (let i = 0, l = bases.length; i < l; ++i) {
    if (bases[i] === cls) {
      return true;
    }
  }
  return this instanceof cls;
}

// ---- method decoration (strict-mode __declareCaller mechanism) --------------
// dojo relied on `arguments.callee` to identify the running method; strict ESM
// forbids it. We wrap each declared function so that, while it runs, it records
// itself as `this.__declareCaller`. The SAME wrapper instance is what lands in the
// prototype (and is copied down mixin chains), so inherited()'s identity checks
// (`proto[name] === caller`) hold exactly as in dojo.
function decorate(name: string, fn: any): any {
  function wrapped(this: any): any {
    const prev = this.__declareCaller;
    this.__declareCaller = wrapped;
    try {
      return fn.apply(this, arguments as any);
    } finally {
      this.__declareCaller = prev;
    }
  }
  (wrapped as any).nom = name;
  (wrapped as any).__mdsRaw = fn;
  return wrapped;
}

function mixOwn(target: any, source: any): void {
  // add props (already-decorated on their own class), skipping a constructor
  for (const name in source) {
    if (name != cname && source.hasOwnProperty(name)) {
      target[name] = source[name];
    }
  }
}

// safe mixin: skip constructor, decorate functions like declare() does
function safeMixin(target: any, source: any): any {
  let name: string; let t: any; let ts: string;
  for (name in source) {
    t = source[name];
    if ((t !== op[name] || !(name in op)) && name != cname) {
      ts = opts.call(t);
      if (ts == '[object Function]' || ts == '[object AsyncFunction]') {
        // non-trivial function method => wrap it (carries nom + __declareCaller)
        t = decorate(name, t);
      }
      target[name] = t;
    }
  }
  return target;
}

function extend(this: any, source: any): any {
  safeMixin(this.prototype, source);
  return this;
}

function createSubclass(this: any, mixins: any, props: any): Ctor {
  // crack parameters
  if (!(mixins instanceof Array || typeof mixins === 'function')) {
    props = mixins;
    mixins = undefined;
  }
  props = props || {};
  mixins = mixins || [];
  return declare([this].concat(mixins), props);
}

// ---- constructor variants ---------------------------------------------------

// chained constructor compatible with the legacy declare()
function chainedConstructor(bases: any[], ctorSpecial: any): any {
  function C(this: any): any {
    let a: any = arguments; const args = a; let a0 = a[0]; let f: any; let i: number; let m: any;
    const l = bases.length; let preArgs: any;

    if (!((this as any) instanceof C)) {
      // not called via new, so force it
      return applyNew(C, a);
    }

    // 1) call two types of the preamble
    if (ctorSpecial && (a0 && a0.preamble || this.preamble)) {
      preArgs = new Array(bases.length);
      preArgs[0] = a;
      for (i = 0; ;) {
        a0 = a[0];
        if (a0) {
          f = a0.preamble;
          if (f) {
            a = f.apply(this, a) || a;
          }
        }
        f = bases[i].prototype;
        f = f.hasOwnProperty('preamble') && f.preamble;
        if (f) {
          a = f.apply(this, a) || a;
        }
        if (++i == l) {
          break;
        }
        preArgs[i] = a;
      }
    }
    // 2) call all non-trivial constructors using prepared arguments
    for (i = l - 1; i >= 0; --i) {
      f = bases[i];
      m = f._meta;
      f = m ? m.ctor : f;
      if (f) {
        const prev = this.__declareCaller;
        this.__declareCaller = f;
        try { f.apply(this, preArgs ? preArgs[i] : a); }
        finally { this.__declareCaller = prev; }
      }
    }
    // 3) continue the original ritual: call the postscript
    f = this.postscript;
    if (f) {
      f.apply(this, args);
    }
  }
  return C;
}

// single-base chained constructor
function singleConstructor(ctor: any, ctorSpecial: any): any {
  function C(this: any): any {
    let a: any = arguments; let t: any = a; const a0 = a[0]; let f: any;

    if (!((this as any) instanceof C)) {
      return applyNew(C, a);
    }

    // 1) call two types of the preamble
    if (ctorSpecial) {
      if (a0) {
        f = a0.preamble;
        if (f) {
          t = f.apply(this, t) || t;
        }
      }
      f = this.preamble;
      if (f) {
        f.apply(this, t);
      }
    }
    // 2) call a constructor
    if (ctor) {
      const prev = this.__declareCaller;
      this.__declareCaller = ctor;
      try { ctor.apply(this, a); }
      finally { this.__declareCaller = prev; }
    }
    // 3) continue the original ritual: call the postscript
    f = this.postscript;
    if (f) {
      f.apply(this, a);
    }
  }
  return C;
}

// plain vanilla constructor (can use inherited() to call its base constructor)
function simpleConstructor(bases: any[]): any {
  function C(this: any): any {
    const a: any = arguments; let i = 0; let f: any; let m: any;

    if (!((this as any) instanceof C)) {
      return applyNew(C, a);
    }

    // 2) call the top constructor (it can use this.inherited())
    for (; f = bases[i]; ++i) { // intentional assignment
      m = f._meta;
      f = m ? m.ctor : f;
      if (f) {
        const prev = this.__declareCaller;
        this.__declareCaller = f;
        try { f.apply(this, a); }
        finally { this.__declareCaller = prev; }
        break;
      }
    }
    // 3) call the postscript
    f = this.postscript;
    if (f) {
      f.apply(this, a);
    }
  }
  return C;
}

function chain(name: string, bases: any[], reversed: boolean): any {
  return function (this: any): any {
    let b: any; let m: any; let f: any; let i = 0; let step = 1;
    if (reversed) {
      i = bases.length - 1;
      step = -1;
    }
    for (; b = bases[i]; i += step) { // intentional assignment
      m = b._meta;
      f = (m ? m.hidden : b.prototype)[name];
      if (f) {
        f.apply(this, arguments as any);
      }
    }
  };
}

// forceNew(ctor): a new object inheriting from ctor.prototype WITHOUT running ctor
function forceNew(ctor: any): any {
  xtor.prototype = ctor.prototype;
  const t = new xtor();
  xtor.prototype = null;
  return t;
}

// applyNew(ctor, args): like `new ctor(...args)` but ctor + args come from args.
// (dojo read the ctor from args.callee; strict mode forces us to pass it.)
function applyNew(ctor: any, args: any): any {
  const t = forceNew(ctor);
  ctor.apply(t, args);
  return t;
}

// ---- declare() --------------------------------------------------------------
// TYPED RETURN (Lever B, "typed outward"): consumers/subclasses of a declared class
// get real types for its members (props + inherited bases) instead of `any`. The props
// object `P` is a BARE inferred type parameter — NO `ThisType` — so props-method `this`
// stays `any` and the ~300 `this.inherited(arguments)` bodies are never newly checked.
// `constructor` is dropped from the instance surface (an object type already has one; a
// plain-function `constructor` member would conflict with a native class's construct
// signature). Placeholder fields like `domNode: null` widen to `any` under strict:false.
type PropsShape<P> = Omit<P, 'constructor'>;
// Members declare() puts on EVERY produced prototype, regardless of props: the className
// (declaredClass) and the MRO helpers. Not in the props object, so they must be added
// explicitly or consumers get TS2339 on `this.declaredClass` / `this.isInstanceOf(...)`.
interface DeclareApi {
  declaredClass: string;
  isInstanceOf(cls: any): boolean;
  inherited(...args: any[]): any;
  getInherited(...args: any[]): any;
}
type DeclareCtor<I> = { new (params?: any, srcNodeRef?: any): I & DeclareApi };
// Named (className is a string): array bases (incl. []) precede single|null at the same
// arity, because `BaseInstance<[A,B]>` (tuple hitting the single-base branch) would
// wrongly collapse to {}. The `T extends readonly any[]` constraint self-rejects for
// non-array args, so single/null calls fall through.
/**
 * **mds-dojo** — faithful C3 `declare()`, the mds re-implementation of Dojo 1.x
 * `declare(className?, superclass, props)`. Same call shapes, but it produces a
 * native-class-interoperable constructor whose `this.inherited(arguments)` walks the
 * prototype chain (no `arguments.callee`), and it has a TYPED return so consumers /
 * subclasses of a declared class see real member types (props + inherited bases)
 * instead of `any`. NOT vanilla Dojo — this is ours.
 *
 * ```ts
 * const MyMixin = declare("more.MyMixin", [_WidgetBase, SomeMixin], {
 *   foo() { this.inherited(arguments); },
 * });
 * ```
 *
 * NEW widgets should prefer the native-class shape (`class X extends {@link compose}(…)`
 * + {@link mdsWidget}); `declare()` is kept for legacy / classic-declare widgets.
 * @param className dotted declared name (registered so a dotted `data-dojo-type` resolves), or omit for anonymous.
 * @param superclass a single base, a base ARRAY (C3 MRO — first is the spine), or `null`.
 * @param props the prototype members (fields + methods).
 * @returns a constructor typed as the composed base instance plus the props.
 */
export function declare<const T extends readonly any[], P>(className: string, superclass: T, props: P): DeclareCtor<ComposedInstance<T> & PropsShape<P>>;
/** **mds-dojo** — faithful C3 `declare()` (typed return). See the named + array-bases overload for details. */
export function declare<S, P>(className: string, superclass: S, props: P): DeclareCtor<BaseInstance<S> & PropsShape<P>>;
// Anonymous (first arg is the base list / base / null):
/** **mds-dojo** — faithful C3 `declare()` (typed return). See the named + array-bases overload for details. */
export function declare<const T extends readonly any[], P>(superclass: T, props: P): DeclareCtor<ComposedInstance<T> & PropsShape<P>>;
/** **mds-dojo** — faithful C3 `declare()` (typed return). See the named + array-bases overload for details. */
export function declare<const T extends readonly any[]>(superclass: T): DeclareCtor<ComposedInstance<T>>;
/** **mds-dojo** — faithful C3 `declare()` (typed return). See the named + array-bases overload for details. */
export function declare<S, P>(superclass: S, props: P): DeclareCtor<BaseInstance<S> & PropsShape<P>>;
// Catch-all safety net (permissive): any shape not matched above yields `Ctor` (instance
// `any`) rather than a "no overload matches" error. Keeps the whole change non-breaking.
/** **mds-dojo** — faithful C3 `declare()` (permissive catch-all overload). See the named + array-bases overload for details. */
export function declare(className?: any, superclass?: any, props?: any): Ctor;
export function declare(className: any, superclass?: any, props?: any): Ctor {
  // crack parameters
  if (typeof className != 'string') {
    props = superclass;
    superclass = className;
    className = '';
  }
  props = props || {};

  let proto: any; let i: number; let t: any; let ctor: any; let name: string; let bases: any; let chains: any;
  let mixins = 1; const parents = superclass;

  // build a prototype
  if (opts.call(superclass) == '[object Array]') {
    // C3 MRO
    bases = c3mro(superclass, className);
    t = bases[0];
    mixins = bases.length - t;
    superclass = bases[mixins];
  } else {
    bases = [0];
    if (superclass) {
      if (opts.call(superclass) == '[object Function]') {
        t = superclass._meta;
        bases = bases.concat(t ? t.bases : superclass);
      } else {
        err('base class is not a callable constructor.', className);
      }
    } else if (superclass !== null) {
      err('unknown base class. Did you use dojo.require to pull it in?', className);
    }
  }

  if (superclass) {
    for (i = mixins - 1; ; --i) {
      proto = forceNew(superclass);
      if (!i) {
        // stop if nothing to add (the last base)
        break;
      }
      // mix in properties
      t = bases[i];
      (t._meta ? mixOwn : mix)(proto, t.prototype);
      // chain in new constructor
      ctor = function () { /* synthetic */ };
      ctor.superclass = superclass;
      ctor.prototype = proto;
      superclass = proto.constructor = ctor;
    }
  } else {
    proto = {};
  }
  // add all properties
  safeMixin(proto, props);
  // add constructor (raw — not decorated; ctor identity tracked by the ctor
  // machinery setting this.__declareCaller around each ctor call)
  t = props.constructor;
  if (t !== op.constructor) {
    t.nom = cname;
    proto.constructor = t;
  }

  // Build `hidden` = the class's OWN members, but with function members pointing
  // at the SAME decorated wrappers now on `proto`. dojo used `hidden = props`
  // (raw), relying on identity `hidden[name] === proto[name] === arguments.callee`.
  // We wrap methods for the strict-mode caller cursor, so `hidden` must hold the
  // wrappers too — otherwise inherited()'s `meta.hidden[name] === caller` fast
  // path misses after aspect.after() replaces proto[name] with a dispatcher, and
  // the fallback scan (proto[name] no longer === caller) recurses infinitely.
  const hidden: any = {};
  for (const k in props) {
    if (props.hasOwnProperty(k)) {
      hidden[k] = (k != cname && typeof props[k] === 'function') ? proto[k] : props[k];
    }
  }

  // collect chains and flags
  for (i = mixins - 1; i; --i) {
    t = bases[i]._meta;
    if (t && t.chains) {
      chains = mix(chains || {}, t.chains);
    }
  }
  if (proto['-chains-']) {
    chains = mix(chains || {}, proto['-chains-']);
  }
  if (superclass && superclass.prototype && superclass.prototype['-chains-']) {
    chains = mix(chains || {}, superclass.prototype['-chains-']);
  }

  // build ctor
  t = !chains || !chains.hasOwnProperty(cname);
  bases[0] = ctor = (chains && chains.constructor === 'manual') ? simpleConstructor(bases)
    : (bases.length == 1 ? singleConstructor(props.constructor, t) : chainedConstructor(bases, t));

  // add meta information to the constructor
  ctor._meta = { bases: bases, hidden: hidden, chains: chains, parents: parents, ctor: props.constructor };
  ctor.superclass = superclass && superclass.prototype;
  ctor.extend = extend;
  ctor.createSubclass = createSubclass;
  ctor.prototype = proto;
  proto.constructor = ctor;

  // add "standard" methods to the prototype
  proto.getInherited = getInherited;
  proto.isInstanceOf = isInstanceOf;
  proto.inherited = inherited;
  proto.__inherited = inherited;

  // add name if specified
  if (className) {
    proto.declaredClass = className;
    lang.setObject(className, ctor);
    if (typeof onDeclare === 'function') { onDeclare(className, ctor); }
  }

  // build chains and add them to the prototype
  if (chains) {
    for (name in chains) {
      if (proto[name] && typeof chains[name] == 'string' && name != cname) {
        t = proto[name] = chain(name, bases, chains[name] === 'after');
        t.nom = name;
      }
    }
  }

  return ctor as Ctor;
}

// Optional hook: lets ./declare.ts register declared classes into the mds parser
// registry (declaredTypes) without this module importing it (avoids a cycle).
let onDeclare: ((name: string, ctor: any) => void) | null = null;
export function setOnDeclare(fn: (name: string, ctor: any) => void): void { onDeclare = fn; }

(declare as any).safeMixin = safeMixin;

export { safeMixin };
export default declare;
